import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { sendWhatsApp } from "@/lib/whatsapp";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Plus, Loader2, Phone, Instagram, MessageCircle, Share2, FormInput, Sparkles,
  AlertTriangle, UserCheck, X, ArrowRight, DollarSign, MoreHorizontal, Trash2,
} from "lucide-react";
import { toast } from "sonner";

const STAGE_DEF = [
  { key: "novo_contato",      label: "Novo contato",      hue: "215 50% 92%" },
  { key: "em_atendimento",    label: "Em atendimento",    hue: "215 60% 88%" },
  { key: "orcamento_enviado", label: "Orçamento enviado", hue: "262 60% 88%" },
  { key: "agendamento_marcado", label: "Agendamento marcado", hue: "199 60% 88%" },
  { key: "compareceu",        label: "Compareceu",        hue: "150 60% 88%" },
  { key: "cliente_ativo",     label: "Cliente ativo",     hue: "142 60% 85%" },
  { key: "perdido",           label: "Perdido",           hue: "0 60% 92%" },
];

const SOURCE_META = {
  instagram:  { icon: Instagram,   label: "Instagram" },
  whatsapp:   { icon: MessageCircle, label: "WhatsApp" },
  indicacao:  { icon: Share2,      label: "Indicação" },
  formulario: { icon: FormInput,   label: "Formulário" },
  manual:     { icon: Sparkles,    label: "Manual" },
  outro:      { icon: Sparkles,    label: "Outro" },
};

function LeadCard({ lead, onChange, onConvert, onDelete }) {
  const Src = SOURCE_META[lead.source]?.icon || Sparkles;
  const canSend = !!lead.phone;
  const message = `Olá ${lead.name}, tudo bem? Estou retornando o contato.`;

  const onDragStart = (e) => {
    e.dataTransfer.setData("text/plain", lead.id);
    e.dataTransfer.effectAllowed = "move";
  };

  return (
    <Card
      draggable
      onDragStart={onDragStart}
      data-testid={`lead-card-${lead.id}`}
      className="p-3 rounded-lg border-border bg-card cursor-grab active:cursor-grabbing hover:shadow-sm transition-shadow space-y-2"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-sm truncate">{lead.name}</div>
          {lead.phone && <div className="text-xs text-muted-foreground flex items-center gap-1 truncate"><Phone className="w-3 h-3" />{lead.phone}</div>}
        </div>
        <Badge variant="outline" className="text-[10px] gap-1 shrink-0"><Src className="w-3 h-3" />{SOURCE_META[lead.source]?.label || lead.source}</Badge>
      </div>
      {lead.notes && <div className="text-xs text-muted-foreground line-clamp-2">{lead.notes}</div>}
      <div className="flex items-center justify-between gap-2">
        {lead.value_estimate > 0 ? (
          <span className="text-xs inline-flex items-center gap-1 text-muted-foreground"><DollarSign className="w-3 h-3" />R$ {lead.value_estimate.toFixed(0)}</span>
        ) : <span />}
        {lead.stuck ? (
          <Badge variant="outline" className="text-[10px] gap-1 bg-amber-500/10 text-amber-700 border-amber-500/30">
            <AlertTriangle className="w-3 h-3" /> Parado há {lead.days_in_stage}d
          </Badge>
        ) : (
          <span className="text-[10px] text-muted-foreground">{lead.days_in_stage}d nessa etapa</span>
        )}
      </div>
      <div className="flex items-center gap-1 pt-1 border-t border-border">
        {canSend && (
          <button type="button"
             onClick={() => sendWhatsApp({ phone: lead.phone, text: message, context: "funnel_lead" })}
             className="text-[10px] inline-flex items-center gap-1 px-2 py-1 rounded text-white"
             style={{ background: `hsl(var(--tenant-primary))` }}
             data-testid={`lead-wa-${lead.id}`}>
            <MessageCircle className="w-3 h-3" /> WhatsApp
          </button>
        )}
        {lead.stage !== "cliente_ativo" && !lead.client_id && (
          <button onClick={onConvert}
                  className="text-[10px] inline-flex items-center gap-1 px-2 py-1 rounded border border-border hover:bg-muted text-muted-foreground"
                  data-testid={`lead-convert-${lead.id}`}>
            <UserCheck className="w-3 h-3" /> Converter
          </button>
        )}
        <button onClick={onDelete}
                className="ml-auto text-[10px] p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                data-testid={`lead-delete-${lead.id}`}
                title="Excluir lead">
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </Card>
  );
}

function Column({ stage, leads, onDrop, onChange, onConvert, onDelete }) {
  const [over, setOver] = useState(false);
  return (
    <div
      className={`w-72 shrink-0 flex flex-col rounded-lg border ${over ? "border-primary bg-primary/5" : "border-border bg-muted/30"} transition-colors`}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const id = e.dataTransfer.getData("text/plain");
        if (id) onDrop(id, stage.key);
      }}
      data-testid={`column-${stage.key}`}
    >
      <div className="px-3 py-2.5 border-b border-border flex items-center gap-2">
        <div className="w-2.5 h-2.5 rounded-full" style={{ background: `hsl(${stage.hue})` }} />
        <div className="font-medium text-sm">{stage.label}</div>
        <Badge variant="outline" className="ml-auto">{leads.length}</Badge>
      </div>
      <div className="flex-1 p-2 space-y-2 min-h-[120px] max-h-[68vh] overflow-y-auto">
        {leads.map((lead) => (
          <LeadCard key={lead.id} lead={lead} onChange={onChange} onConvert={() => onConvert(lead)} onDelete={() => onDelete(lead)} />
        ))}
        {leads.length === 0 && (
          <div className="text-[11px] text-muted-foreground text-center py-6">Solte aqui</div>
        )}
      </div>
    </div>
  );
}

export default function TenantFunnel() {
  const { slug } = useParams();  // eslint-disable-line no-unused-vars
  const [board, setBoard] = useState({ stages: {}, totals: { total: 0, stuck: 0, value: 0 } });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", source: "manual", value_estimate: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/tenant/leads/board");
      setBoard(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const moveStage = async (leadId, newStage) => {
    try {
      await api.put(`/tenant/leads/${leadId}`, { stage: newStage });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, value_estimate: Number(form.value_estimate || 0) };
      if (!payload.email) delete payload.email;
      await api.post("/tenant/leads", payload);
      toast.success("Lead cadastrado");
      setForm({ name: "", phone: "", email: "", source: "manual", value_estimate: "", notes: "" });
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const convert = async (lead) => {
    if (!window.confirm(`Converter "${lead.name}" em cliente?`)) return;
    try {
      await api.post(`/tenant/leads/${lead.id}/convert`);
      toast.success("Lead convertido em cliente");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (lead) => {
    if (!window.confirm(`Excluir lead "${lead.name}"?`)) return;
    try {
      await api.delete(`/tenant/leads/${lead.id}`);
      toast.success("Lead removido");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) {
    return <div className="p-10"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  }

  return (
    <div className="p-6 md:p-10 space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Funil de Vendas</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Pipeline comercial</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Arraste leads entre as etapas. {board.totals.stuck > 0 && <span className="text-amber-700 font-medium">{board.totals.stuck} parado(s) há 3+ dias.</span>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Valor em pipeline</div>
            <div className="font-display text-2xl font-bold">R$ {board.totals.value.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</div>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="funnel-new-lead" className="gap-2"
                style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}>
                <Plus className="w-4 h-4" /> Novo lead
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-display">Novo lead</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Nome</Label>
                  <Input required data-testid="lead-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Telefone</Label>
                    <Input data-testid="lead-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(11) 99999-0000" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>E-mail (opcional)</Label>
                    <Input type="email" data-testid="lead-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Origem</Label>
                    <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                      <SelectTrigger data-testid="lead-source"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(SOURCE_META).map(([k, m]) => (
                          <SelectItem key={k} value={k}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Valor estimado (R$)</Label>
                    <Input type="number" min={0} step="0.01" data-testid="lead-value" value={form.value_estimate} onChange={(e) => setForm({ ...form, value_estimate: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Observações</Label>
                  <textarea
                    rows={3}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="w-full px-3 py-2 text-sm rounded-md border border-border bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    placeholder="Quer escova, perguntou sobre coloração..."
                  />
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={busy} data-testid="lead-submit" className="gap-2"
                    style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}>
                    {busy && <Loader2 className="w-4 h-4 animate-spin" />} Cadastrar lead
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-3">
        {STAGE_DEF.map((stage) => (
          <Column
            key={stage.key}
            stage={stage}
            leads={board.stages[stage.key] || []}
            onDrop={moveStage}
            onChange={load}
            onConvert={convert}
            onDelete={remove}
          />
        ))}
      </div>
    </div>
  );
}
